import React from "react";
import { useViewerCopy } from "../../utils/viewerLanguage";
import { isSenderContext } from "../../utils/cookieConsentUtils";

const VIDEO_DOMAINS = /youtube\.com|youtu\.be|vimeo\.com|wistia\.(com|net)|dailymotion\.com|loom\.com|vidyard\.com/i;
const AUDIO_DOMAINS = /soundcloud\.com|spotify\.com/i;

export const detectContentType = (element) => {
  try {
    const url = element?.value?.video_link || "";
    if (VIDEO_DOMAINS.test(url)) return "video";
    if (AUDIO_DOMAINS.test(url)) return "audio";
    const type = element?.element_type_name?.toLowerCase() || "";
    if (type === "video") return "video";
    if (type === "audio") return "audio";
  } catch (err) { console.warn(err); }
  return "content";
};

const CookieBlockedMessage = ({ style = {}, onClick, dimensions, contentType = "content", large = false }) => {
  const viewerLanguage = useViewerCopy();
  const consent = viewerLanguage.copy?.consent;
  const kind = ["video", "audio", "image"].includes(contentType) ? contentType : "content";
  const defaultTitle = contentType === "video" ? "Video Unavailable"
              : contentType === "audio" ? "Audio Unavailable"
              : contentType === "image" ? "Image Unavailable"
              : "Content Unavailable";
  const defaultHint = contentType === "video" ? "Accept all cookies to watch this video."
             : contentType === "audio" ? "Accept all cookies to play this audio."
             : contentType === "image" ? "Accept all cookies to show this image."
             : "Accept all cookies to view this content.";
  const sender = isSenderContext();
  const title = consent && !sender ? consent[`blocked_${kind}`] : defaultTitle;
  const hint = sender ? `Third-party content is blocked. Accept all cookies to preview this ${kind}.`
             : consent ? consent[`hint_${kind}`] : defaultHint;
  const handleOpenCookieSettings = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("openCookieConsentModal"));
    }
  };

  // Determine layout mode from the element's physical dimensions.
  // In portrait on small phones the element area can be as little as 150×70px,
  // which isn't tall enough to show icon + title + subtitle + button without clipping.
  const containerH = parseFloat(style.height) || 0;
  const containerW = parseFloat(style.width)  || 0;

  // Full layout: element is large enough to show everything (landscape / tablet / big element).
  // Compact layout: limited height — drop description text and shrink icon.
  // Mini layout: very small element — icon hidden, one-line title + tiny button only.
  const isMini    = !large && (containerH < 80  || containerW < 130);
  const isCompact = !large && !isMini && (containerH < 140 || containerW < 200);

  const iconSize     = large ? 44 : isMini ? 0  : isCompact ? 20 : 28;
  const titleSize    = large ? "20px" : isMini ? "9px" : isCompact ? "10px" : "11px";
  const hintSize     = large ? "14px" : "10px";
  const btnFontSize  = large ? "14px" : isMini ? "8px" : isCompact ? "9px"  : "10px";
  const btnPadding   = large ? "8px 18px" : isMini ? "3px 7px" : isCompact ? "3px 8px" : "4px 10px";
  const outerPadding = large ? "24px" : isMini ? "4px" : isCompact ? "8px 6px" : "12px 8px";

  return (
    <div
      style={{
        position: "relative",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#f0f4f8",
        border: "1.5px solid #c8d6e5",
        borderRadius: "6px",
        padding: outerPadding,
        textAlign: "center",
        overflow: "hidden",
        cursor: onClick ? "pointer" : "default",
        zIndex: 10,
        ...style,
      }}
      onClick={onClick}
      title={hint}
      dir={consent ? viewerLanguage.dir : undefined}
      lang={consent ? viewerLanguage.locale : undefined}
    >
      {iconSize > 0 && (
        <svg
          width={iconSize}
          height={iconSize}
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          style={{ opacity: 0.55, flexShrink: 0, marginBottom: large ? "12px" : isCompact ? "4px" : "6px" }}
        >
          <path
            d="M9 16H9.01M12 11H12.01M7 10H7.01M15 16H15.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C12 5.76142 13.7909 8 16 8C16 10.2091 18.2386 12 21 12Z"
            stroke="#5a7fa8"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}

      <div
        style={{
          fontWeight: "700",
          fontSize: titleSize,
          color: "#2c3e50",
          marginBottom: large ? "8px" : isMini ? "3px" : "4px",
          lineHeight: "1.3",
          whiteSpace: "nowrap",
        }}
      >
        {title}
      </div>

      {!isMini && !isCompact && (
        <div
          style={{
            fontSize: hintSize,
            color: "#666",
            lineHeight: "1.4",
            marginBottom: large ? "14px" : "8px",
            maxWidth: large ? "320px" : "180px",
          }}
        >
          {hint}
        </div>
      )}

      <button
        type="button"
        onClick={handleOpenCookieSettings}
        style={{
          background: "#294561",
          color: "#fff",
          border: "none",
          borderRadius: "4px",
          width: "auto",
          minWidth: 0,
          flex: "0 0 auto",
          alignSelf: "center",
          textTransform: "none",
          padding: btnPadding,
          fontSize: btnFontSize,
          fontWeight: "600",
          cursor: "pointer",
          whiteSpace: "nowrap",
          marginTop: isCompact ? "2px" : 0,
        }}
        onMouseEnter={(e) => (e.currentTarget.style.background = "#1d3348")}
        onMouseLeave={(e) => (e.currentTarget.style.background = "#294561")}
      >
        {consent ? consent.settings_button : "Cookie settings"}
      </button>
    </div>
  );
};

export default CookieBlockedMessage;
